import React, { useCallback, useMemo, useState } from "react";
import {
  makeStyles,
  Popover,
  Grid,
  OutlinedInput,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Box
} from "@material-ui/core";
import { connect } from "react-redux";
import { sortRouteGroupsFn } from "../../utils/helpers";
import { routeColors } from "../data/constants";
import { setShowingRouteId } from "../../store/actions/project";
import DirectionsCarIcon from "@material-ui/icons/DirectionsCar";
import { FormattedText } from "../common/FormattedText";

const useStyles = makeStyles(theme => ({
  menuPaper: {
    width: 320,
    boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.16)",
    maxHeight: 305
  },
  menuContentWrapper: {
    height: 305,
    maxHeight: 305,
    overflow: "hidden"
  },
  searchWrapper: {
    padding: theme.spacing(1.25, 1.1),
    borderBottom: "1px solid #EAEBF0"
  },
  searchInput: {
    padding: theme.spacing(1.5)
  },
  routesTitle: {
    fontSize: theme.typography.pxToRem(14),
    color: "#7D7E82",
    margin: theme.spacing(1, 0, 1, 2)
  },
  listWrapper: {
    padding: theme.spacing(1, 0),
    overflowY: "auto"
  },
  list: {
    padding: 0
  },
  listItem: {
    padding: theme.spacing(0.93, 2.25)
  },
  listItemText: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightRegular,
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
    color: "#202124",
    maxWidth: 170
  },
  listItemStops: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightRegular,
    color: "#7D7E82"
  },
  truckIconLink: {
    boxSizing: "border-box",
    display: "block",
    width: 24,
    height: 24,
    borderRadius: 4,
    paddingTop: 3.5,
    paddingLeft: 5,
    paddingRight: 5,
    marginRight: theme.spacing(2)
  },
  truckIcon: {
    color: "#fff",
    fontSize: theme.typography.pxToRem(14)
  }
}));

const RouteHeaderRoutesMenu = props => {
  const {
    solution,
    colorsObj,
    menuOpen,
    closeMenu,
    menuButtonRef,
    setShowingRouteId,
    resetStopsSearchInput
  } = props;

  // General
  const classes = useStyles();

  // States
  const [searchInputValue, setSearchInputValue] = useState("");
  const [filteredItems, setFilteredItems] = useState([]);

  // Callbacks
  const getColor = useCallback(
    id => (colorsObj[id] >= 0 ? colorsObj[id] : 0),
    []
  );

  // Memos
  const listItems = useMemo(() => {
    const theListItems = [];
    Object.keys(solution.routeGroups)
      .sort(sortRouteGroupsFn)
      .forEach(groupId => {
        solution.routeGroups[groupId].forEach(routes => {
          const routesStops = routes
            .map(route => route.stops)
            .flat()
            .filter(stop => stop.type !== "start" && stop.type !== "end");
          if (routesStops.length > 0) {
            const mainRouteId = routes[0].id;
            theListItems.push({
              color: routeColors[getColor(mainRouteId) % routeColors.length],
              routeId: mainRouteId,
              vehicleName: routes[0].vehicle_name,
              stopsCount: routes
                .map(route => route.stops)
                .flat()
                .filter(stop => stop.type !== "start" && stop.type !== "end")
                .length
            });
          }
        });
      });
    setFilteredItems(theListItems);
    return theListItems;
  }, [getColor, solution.routeGroups]);

  return (
    <>
      <Popover
        open={menuOpen}
        onClose={closeMenu}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left"
        }}
        anchorEl={menuButtonRef.current}
        PaperProps={{
          elevation: 0
        }}
        classes={{
          paper: classes.menuPaper
        }}
      >
        <Grid
          container
          direction="column"
          className={classes.menuContentWrapper}
        >
          <Grid item className={classes.searchWrapper}>
            <OutlinedInput
              placeholder={"Switch to..."}
              fullWidth
              classes={{ input: classes.searchInput }}
              onChange={event => {
                setSearchInputValue(event.target.value.toLowerCase());
                setFilteredItems(
                  listItems.filter(item =>
                    item.vehicleName
                      .toLowerCase()
                      .includes(event.target.value.toLowerCase())
                  )
                );
              }}
            />
          </Grid>
          <Grid
            item
            alignItems={"center"}
            alignContent={"center"}
            justify={"center"}
            xs
            className={classes.listWrapper}
          >
            <Typography className={classes.routesTitle}>Routes</Typography>
            <List className={classes.list}>
              {filteredItems.map(item => (
                <ListItem
                  key={item.routeId}
                  button
                  onClick={() => {
                    closeMenu();
                    resetStopsSearchInput();
                    setShowingRouteId(item.routeId);
                  }}
                  className={classes.listItem}
                >
                  <Box
                    className={classes.truckIconLink}
                    style={{
                      backgroundColor: item.color
                    }}
                  >
                    <DirectionsCarIcon className={classes.truckIcon} />
                  </Box>
                  <Grid container>
                    <Grid item xs>
                      <ListItemText
                        disableTypography
                        className={classes.listItemText}
                      >
                        <FormattedText
                          text={item.vehicleName}
                          query={searchInputValue}
                        />
                      </ListItemText>
                    </Grid>
                    <Grid item>
                      <ListItemText
                        disableTypography
                        className={classes.listItemStops}
                      >
                        {`${item.stopsCount} stop${
                          item.stopsCount !== 1 ? "s" : ""
                        }`}
                      </ListItemText>
                    </Grid>
                  </Grid>
                </ListItem>
              ))}
            </List>
          </Grid>
        </Grid>
      </Popover>
    </>
  );
};

const mapStateToProps = state => ({
  solution: state.project.solution,
  colorsObj: state.project.solution && state.project.solution.routeColors
});

export default connect(mapStateToProps, { setShowingRouteId })(
  RouteHeaderRoutesMenu
);
